import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';

import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {Device} from '../../../../shared/iot-platform/model/iot-device';
import {SessionService} from '../../../../shared/service/session.service';
import {AuthorizationComponent} from '../../../authorization/authorization.component';
import {ValidatorMessage} from '../../create/properties/validator/validator-enum';
import {UpdateSensorDtoFactory} from '../../factory/update-sensor-dto.factory';
import {Feature} from '../../model/feature.model';
import {Sensor} from '../../model/sensor.model';
import {UpdateSensorDto} from '../../model/update-sensor-dto.model';
import {SensorService} from '../../service/sensor.service';
import {SensorModalInterface, sensorModalValues} from './properties/sensor-modal-properties';
import {UpdateSensorFormConfig} from './properties/update-sensor-form';
import {UpdateSensorFormController} from './properties/update-sensor-form-controller';
import {UpdateLocationComponent} from './update-location/update-location.component';

@Component({
  selector: 'app-sensor-detail-info',
  templateUrl: './sensor-detail-info.component.html',
  styleUrls: ['./sensor-detail-info.component.scss'],
  standalone: false,
})
export class SensorDetailInfoComponent extends AuthorizationComponent implements OnInit, OnDestroy {
  public sensorModalValues: SensorModalInterface[] = sensorModalValues;

  public sensor: Sensor;
  public device: Device;
  public features: Feature[];

  public isEditSensor: boolean = false;
  public saving: boolean = false;

  public updateSensorForm: UntypedFormGroup;
  public updateSensorFormController: UpdateSensorFormController;

  public validatorMessage = ValidatorMessage;
  public updateSensorDtoFactory: UpdateSensorDtoFactory = new UpdateSensorDtoFactory();

  private observableGuard = new Subject<void>();

  @ViewChild('locationModal')
  public locationModal: UpdateLocationComponent;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private sensorService: SensorService,
    sessionService: SessionService
  ) {
    super(sessionService);
    this.setAuthorization('/sensors', ['readOne', 'update']);
    this.sessionService.sharedSensor.pipe(
      takeUntil(this.observableGuard))
      .subscribe((sensor: Sensor) => {
        this.sensor = sensor;
      });
    this.sessionService.sharedFeatures.pipe(
      takeUntil(this.observableGuard))
      .subscribe((features: Feature[]) => {
        this.features = features;
      });
  }

  public ngOnInit(): void {
    this.device = this.route.snapshot.data['device'];
    this.updateSensorForm = this.formBuilder.group(UpdateSensorFormConfig);
    this.updateSensorFormController = new UpdateSensorFormController(this.updateSensorForm);
    this.updateSensorFormController.resetUpdateSensorForm(this.sensor);
  }

  public ngOnDestroy(): void {
    this.observableGuard.next();
    this.observableGuard.complete();
  }

  public onEditClick(): void {
    this.updateSensorFormController.resetUpdateSensorForm(this.sensor);
    this.isEditSensor = true;
  }

  public onCancelClick(): void {
    this.isEditSensor = false;
  }

  public onSubmitSensorChanges(): void {
    this.submitChanges();
    this.isEditSensor = false;
  }

  public submitChanges(): void {
    const updateSensorDto: UpdateSensorDto = this.updateSensorDtoFactory.create(
      this.updateSensorFormController.startDateISOString,
      this.updateSensorFormController.endDateISOString,
      this.updateSensorFormController.instrumentType,
      this.updateSensorFormController.locationName,
      this.updateSensorFormController.xcoordinate,
      this.updateSensorFormController.ycoordinate,
      this.updateSensorFormController.zcoordinate
    );

    this.saving = true;

    this.sensorService.updateSensor(updateSensorDto, this.sensor.id).subscribe(() => {
      this.sensorService.getSensor(this.sensor.id).subscribe((sensor: Sensor) => {
        this.sessionService.updateSharedSensor(sensor);
      });
      this.hideModalAndStopSaving();
    }, () => {
      this.hideModalAndStopSaving();
    });
  }

  private hideModalAndStopSaving() {
    this.locationModal.hide();
    this.saving = false;
  }
}
